'use client'

import { useMemo, useState } from 'react'

export type AddressType = 'apartment' | 'house' | 'office'

export type AddressFormValues = {
  city: string
  duureg: string
  khoroo: string
  bair: string
  toot: string
  houseNum: string
  street: string
  building: string
  floor: string
  officeName: string
  additionalAddress: string
  type: AddressType
}

type UseAddressFormArgs = {
  addressField: any
  vendor: any
  initial?: any
}

export function useAddressForm({ addressField, vendor, initial }: UseAddressFormArgs) {
  const [city, setCity] = useState<string>(initial?.city || '')
  const [duureg, setDuureg] = useState<string>(initial?.duureg || '')
  const [khoroo, setKhoroo] = useState<string>(initial?.khoroo || '')
  const [addressType, setAddressType] = useState<AddressType>(initial?.type || 'apartment')
  const [bair, setBair] = useState<string>(initial?.bair || '')
  const [toot, setToot] = useState<string>(initial?.toot || '')
  const [houseNum, setHouseNum] = useState<string>(initial?.houseNum || '')
  const [street, setStreet] = useState<string>(initial?.street || '')
  const [building, setBuilding] = useState<string>(initial?.building || '')
  const [floor, setFloor] = useState<string>(initial?.floor || '')
  const [officeName, setOfficeName] = useState<string>(initial?.officeName || '')
  const [additionalAddress, setAdditionalAddress] = useState<string>(initial?.additionalAddress || '')

  const [selectedProvince, setSelectedProvince] = useState<string | null>(initial?.city || null)
  const [selectedDistrict, setSelectedDistrict] = useState<string | null>(initial?.duureg || null)
  const [selectedSubdistrict, setSelectedSubdistrict] = useState<string | null>(initial?.khoroo || null)

  const [prevInitialId, setPrevInitialId] = useState<string | undefined>(initial?.id)
  if (initial?.id && initial.id !== prevInitialId) {
    setPrevInitialId(initial.id)
    setCity(initial.city || '')
    setDuureg(initial.duureg || '')
    setKhoroo(initial.khoroo || '')
    setAddressType(initial.type || 'apartment')
    setBair(initial.bair || '')
    setToot(initial.toot || '')
    setHouseNum(initial.houseNum || '')
    setStreet(initial.street || '')
    setBuilding(initial.building || '')
    setFloor(initial.floor || '')
    setOfficeName(initial.officeName || '')
    setAdditionalAddress(initial.additionalAddress || '')
    setSelectedProvince(initial.city || null)
    setSelectedDistrict(initial.duureg || null)
    setSelectedSubdistrict(initial.khoroo || null)
  }

  const filteredDistricts = useMemo(() => {
    const allDistricts = addressField?.find((p: any) => p.value === selectedProvince)?.children || []
    if (!vendor?.deliveryAreas || vendor.deliveryAreas.length === 0) return allDistricts
    const allowedDistrictIds = new Set(vendor.deliveryAreas.map((a: any) => a.districtId))
    return allDistricts.filter((d: any) => allowedDistrictIds.has(d.id))
  }, [addressField, selectedProvince, vendor?.deliveryAreas])

  const filteredSubdistricts = useMemo(() => {
    const district = filteredDistricts?.find((d: any) => d.value === selectedDistrict)
    if (!district) return []
    const allSubdistricts = district.subchildren || []
    if (!vendor?.deliveryAreas || vendor.deliveryAreas.length === 0) return allSubdistricts
    const areaEntry = vendor.deliveryAreas.find((a: any) => a.districtId === district.id)
    if (!areaEntry) return []
    if (areaEntry.khorooIds.length === 0) return allSubdistricts
    const allowedKhorooIds = new Set(areaEntry.khorooIds)
    return allSubdistricts.filter((k: any) => allowedKhorooIds.has(k.id))
  }, [filteredDistricts, selectedDistrict, vendor?.deliveryAreas])

  const provinces = useMemo(() => {
    if (!addressField) return []
    if (!vendor?.deliveryAreas || vendor.deliveryAreas.length === 0) {
      return addressField.map((p: any) => ({ value: p.value, label: p.value }))
    }
    const allowedDistrictIds = new Set(vendor.deliveryAreas.map((a: any) => a.districtId))
    return addressField
      .filter((p: any) => p.children?.some((d: any) => allowedDistrictIds.has(d.id)))
      .map((p: any) => ({ value: p.value, label: p.value }))
  }, [addressField, vendor?.deliveryAreas])

  const districts = useMemo(() => filteredDistricts.map((d: any) => ({ value: d.value, label: d.value })), [filteredDistricts])
  const subdistricts = useMemo(() => filteredSubdistricts.map((s: any) => ({ value: s.value, label: s.value })), [filteredSubdistricts])

  if (provinces.length > 0 && !selectedProvince) {
    setSelectedProvince(provinces[0].value)
    setCity(provinces[0].value)
  }
  if (filteredDistricts.length > 0 && !selectedDistrict) {
    setSelectedDistrict(filteredDistricts[0].value)
    setDuureg(filteredDistricts[0].value)
  }
  if (filteredSubdistricts.length > 0 && !selectedSubdistrict) {
    setSelectedSubdistrict(filteredSubdistricts[0].value)
    setKhoroo(filteredSubdistricts[0].value)
  }

  const onProvinceChange = (value: string) => {
    setSelectedProvince(value); setCity(value)
    setSelectedDistrict(null); setDuureg('')
    setSelectedSubdistrict(null); setKhoroo('')
  }
  const onDistrictChange = (value: string) => {
    setSelectedDistrict(value); setDuureg(value)
    setSelectedSubdistrict(null); setKhoroo('')
  }
  const onSubdistrictChange = (value: string) => {
    setSelectedSubdistrict(value); setKhoroo(value)
  }

  const reset = () => {
    setCity(''); setDuureg(''); setKhoroo('')
    setAddressType('apartment')
    setBair(''); setToot(''); setHouseNum(''); setStreet('')
    setBuilding(''); setFloor(''); setOfficeName(''); setAdditionalAddress('')
    setSelectedProvince(null); setSelectedDistrict(null); setSelectedSubdistrict(null)
  }

  const validate = (): { ok: true } | { ok: false; error: string } => {
    if (!city || !duureg || !khoroo) return { ok: false, error: 'Хаягийн мэдээллийг бүрэн оруулна уу' }
    if (addressType === 'apartment' && (!bair || !toot)) return { ok: false, error: 'Байр болон тоот оруулна уу' }
    if (addressType === 'house' && !houseNum) return { ok: false, error: 'Хашааны дугаар оруулна уу' }
    if (addressType === 'office' && (!building || !floor || !toot)) return { ok: false, error: 'Барилга, давхар, тоот оруулна уу' }
    return { ok: true }
  }

  const compose = (): any => {
    const body: any = { city, duureg, khoroo, additionalAddress, type: addressType }
    if (addressType === 'apartment') {
      body.bair = bair
      body.toot = toot
    } else if (addressType === 'house') {
      body.houseNum = houseNum
      body.street = street
    } else if (addressType === 'office') {
      body.building = building
      body.floor = floor
      body.toot = toot
      if (officeName) body.officeName = officeName
    }
    return body
  }

  return {
    addressType, setAddressType,
    city, duureg, khoroo,
    bair, setBair, toot, setToot,
    houseNum, setHouseNum, street, setStreet,
    building, setBuilding, floor, setFloor,
    officeName, setOfficeName,
    additionalAddress, setAdditionalAddress,
    selectedProvince, selectedDistrict, selectedSubdistrict,
    onProvinceChange, onDistrictChange, onSubdistrictChange,
    provinces, districts, subdistricts,
    validate, compose, reset,
  }
}
